import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Car, Lock } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import { toast } from 'sonner';
import { useAuth } from '../../../../hooks/AuthenticateContext';
import api from '../../../../services/api';

type VehicleType = 'TRUCK' | 'CARRETA' | 'BITREM';

type Gate = {
  numero_cancela: string | number;
  id_operacao_patio_cancela?: number;
  iss_id_camera_dianteira?: number | string | null;
  ativo?: boolean | number | string | null;
};

// A listagem de cancelas não filtra por `ativo`, então uma cancela desativada
// (ou que mudou de sentido) ainda vem na resposta. Só as ativas podem ser
// operadas manualmente.
const isGateAtiva = (gate: Gate): boolean =>
  gate.ativo === true || gate.ativo === 1 || gate.ativo === 't' || gate.ativo === 'true';

const CancelaManual: React.FC = () => {
  const { user } = useAuth();
  const [selectedGate, setSelectedGate] = useState<string | null>(null);
  const [licensePlate, setLicensePlate] = useState('');
  const [vehicleType, setVehicleType] = useState<VehicleType>('TRUCK');
  const [isLoading, setIsLoading] = useState(false);
  const [dbGates, setDbGates] = useState<{ entry: Gate[], exit: Gate[] }>({ entry: [], exit: [] });
  const isExitGateSelected = selectedGate?.startsWith('exit-') ?? false;

  useEffect(() => {
    const fetchGates = async () => {
      try {
        const [entryRes, exitRes] = await Promise.all([
           api.post("/listar/cancelasEntrada", {}),
           api.post("/listar/cancelasSaida", {})
        ]);
        
        const entryList: Gate[] = Array.isArray(entryRes.data) ? entryRes.data : [];
        const exitList: Gate[] = Array.isArray(exitRes.data) ? exitRes.data : [];

        setDbGates({
          entry: entryList.filter(isGateAtiva),
          exit: exitList.filter(isGateAtiva)
        });
      } catch (error) {
        console.error("Erro ao buscar cancelas:", error);
      }
    };
    fetchGates();
  }, []);

  // As cancelas (número, câmera e tipo entrada/saída) vêm do cadastro em
  // Operações no Pátio > Cancelas. Nada é fixo aqui: quando uma cancela muda de
  // entrada para saída, ou deixa de existir, a tela acompanha automaticamente.
  const entryGates = dbGates.entry.map((gate) => String(gate.numero_cancela));
  const exitGates = dbGates.exit.map((gate) => String(gate.numero_cancela));

  const findGate = (gateKey: string): Gate | undefined => {
    const [tipo, numero] = gateKey.split('-');
    const list = tipo === 'entry' ? dbGates.entry : dbGates.exit;
    return list.find((gate) => String(gate.numero_cancela) === numero);
  };

  const handleGateSelect = (gate: string) => {
    setSelectedGate(gate);
  };

  const getCamId = (gateKey: string): number | null => {
    const camId = findGate(gateKey)?.iss_id_camera_dianteira;
    if (camId === null || camId === undefined || camId === '') return null;
    return Number(camId);
  };

  const handleSubmit = async () => {
    if (!selectedGate) {
      toast.warning('Selecione um portão.');
      return;
    }

    if (!licensePlate) {
       toast.warning('Insira a placa do veículo.');
       return;
    }

    const camId = getCamId(selectedGate);
    if (!camId) {
      toast.error('Erro ao identificar a câmera do portão selecionado.');
      return;
    }
    
    const userId = user?.id || 
                   JSON.parse(localStorage.getItem('user') || '{}').id || 
                   JSON.parse(localStorage.getItem('user') || '{}').id;

    if (!userId) {
       toast.error('Usuário não identificado. Faça login novamente.');
       return;
    }

    const payload = {
      app: 'SecurOS',
      dataHora: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      placa: licensePlate.replace('-', '').toUpperCase(), 
      camId: camId,
      id_usuario_historico: userId
    };

    try {
      setIsLoading(true);
      const response = await api.post('/api', payload);
      
      const responseData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

      if (responseData && responseData.result === true) {
        toast.success('Cancela aberta com sucesso!', {
          duration: 10000,
        });
        
        // Registrar movimentação após sucesso
        await registerMovement(userId);

      } else {
        toast.error('Veículo não liberado, procure a administração', {
           duration: 10000,
        });
      }

      setLicensePlate('');
      setSelectedGate(null);
    } catch (error) {
      console.error('Erro ao abrir cancela:', error);
      toast.error('Erro ao enviar comando de abertura.', {
        duration: 30000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const registerMovement = async (userId: number) => {
      if (!selectedGate) return;

      const isEntry = selectedGate.startsWith('entry-');
      const gateNumber = selectedGate.split('-')[1];

      const gateId = findGate(selectedGate)?.id_operacao_patio_cancela;

      if (!gateId) {
          console.error("ID da cancela não encontrado para o número:", gateNumber);
          toast.error("Erro interno: ID da cancela não identificado.");
          return;
      }

      const body = {
        data_hora: format(new Date(), "yyyy-MM-dd HH:mm:ss", { locale: ptBR }),
        placa_dianteira: licensePlate.replace("-", "").toUpperCase(),
        placa_traseira: null,
        ...(isEntry && {
          numero_partes_nao_motorizada: vehicleType === 'CARRETA' ? 1 : vehicleType === 'BITREM' ? 2 : 0,
        }),
        identificadores_conteineres: null,
        id_operacao_patio_cancela: isEntry ? gateId : null,
        id_operacao_patio_cancela_saida: !isEntry ? gateId : null,
        data_hora_saida: !isEntry ? format(new Date(), "yyyy-MM-dd HH:mm:ss", { locale: ptBR }) : null,
        ativo: true,
        id_usuario_historico: userId,
        status: 1,
      };

      try {
          await api.post('cadastrar/entradaSaidaVeiculos', body);
      } catch (e) {
          console.error("Erro ao registrar movimentação:", e);
      }
  };

  return (
    <div className="h-full flex flex-col items-center p-5 overflow-y-auto bg-[#F5F5F5]">
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-lg overflow-hidden flex flex-col flex-1">
        
        {/* Header */}
        <div className="p-8 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 mb-2">
            <Car className="h-6 w-6 text-[#062D4E]" />
            <h1 className="text-2xl font-bold text-[#062D4E]">Cancela Manual</h1>
          </div>
          <p className="text-gray-500 text-sm">
            Selecione o portão e insira a placa para liberar o acesso.
          </p>
        </div>

        <div className="p-8 space-y-8 flex-1 overflow-y-auto">
          
          {/* Gate Selection Section */}
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6 flex items-center gap-2">
              <span className="text-lg">⇄</span> Seleção de Portão
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              
              {/* Entry Gates */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600 font-medium mb-4">
                  <span>→</span> Entrada
                </div>
                {entryGates.length === 0 && (
                  <p className="text-sm text-gray-400">Nenhuma cancela de entrada cadastrada.</p>
                )}
                <div className="grid grid-cols-3 gap-3">
                  {entryGates.map((gate) => (
                    <button
                      key={`entry-${gate}`}
                      onClick={() => handleGateSelect(`entry-${gate}`)}
                      className={`
                        h-16 rounded-lg text-xl font-bold transition-all relative
                        border-2 
                        ${selectedGate === `entry-${gate}` 
                          ? 'border-[#062D4E] bg-blue-50 text-[#062D4E]' 
                          : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      {gate}
                      <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${selectedGate === `entry-${gate}` ? 'bg-[#062D4E]' : 'bg-gray-300'}`}></div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Exit Gates */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-red-500 font-medium mb-4">
                  <span>←</span> Saída
                </div>
                {exitGates.length === 0 && (
                  <p className="text-sm text-gray-400">Nenhuma cancela de saída cadastrada.</p>
                )}
                <div className="grid grid-cols-3 gap-3">
                  {exitGates.map((gate) => (
                    <button
                      key={`exit-${gate}`}
                      onClick={() => handleGateSelect(`exit-${gate}`)}
                      className={`
                        h-16 rounded-lg text-xl font-bold transition-all relative
                        border-2 
                        ${selectedGate === `exit-${gate}` 
                          ? 'border-[#062D4E] bg-blue-50 text-[#062D4E]' 
                          : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      {gate}
                       <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${selectedGate === `exit-${gate}` ? 'bg-[#062D4E]' : 'bg-gray-300'}`}></div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Separation Line */}
          <hr className="border-gray-100" />

          {/* Vehicle Data Section */}
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-6 flex items-center gap-2">
               <Car className="h-4 w-4" /> Dados do Veículo
            </h3>
            
            <div className={`grid grid-cols-1 gap-8 items-start ${isExitGateSelected ? '' : 'lg:grid-cols-2'}`}>
              {/* Vehicle Type Selection */}
              {!isExitGateSelected && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    Tipo de Veículo
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: 'TRUCK' as VehicleType, label: 'Truck', icon: (active: boolean) => (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 4H15V15H1V4Z" fill={active ? "white" : "#6B7280"} />
                          <path d="M15 8H19L22 11V15H15V8Z" fill={active ? "white" : "#6B7280"} />
                          <circle cx="5" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                          <circle cx="18" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                        </svg>
                      )},
                      { id: 'CARRETA' as VehicleType, label: 'Carreta', icon: (active: boolean) => (
                        <svg width="28" height="24" viewBox="0 0 28 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                           <path d="M2 4H16V15H2V4Z" fill={active ? "white" : "#6B7280"} />
                           <path d="M18 8H21L24 11V15H18V8Z" fill={active ? "white" : "#6B7280"} />
                           <circle cx="5" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                           <circle cx="12" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                           <circle cx="21" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                        </svg>
                      )},
                      { id: 'BITREM' as VehicleType, label: 'Bitrem', icon: (active: boolean) => (
                         <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="0" y="4" width="8" height="11" fill={active ? "white" : "#6B7280"} />
                          <rect x="10" y="4" width="8" height="11" fill={active ? "white" : "#6B7280"} />
                          <path d="M20 8H24L27 11V15H20V8Z" fill={active ? "white" : "#6B7280"} />
                          <circle cx="4" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                          <circle cx="14" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                          <circle cx="23" cy="18" r="3" fill={active ? "white" : "#6B7280"} />
                        </svg>
                      )}
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setVehicleType(type.id)}
                        className={`
                          flex flex-col items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all h-28
                          ${vehicleType === type.id
                            ? 'bg-[#005694] border-[#005694] text-white shadow-md'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                          }
                        `}
                      >
                        <div className="transform scale-110">
                          {type.icon(vehicleType === type.id)}
                        </div>
                        <span className="text-sm font-bold">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* License Plate Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 block">
                  Placa do Veículo
                </label>
                <div className="relative">
                   <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="bg-gray-400 text-white text-[10px] px-1 rounded font-bold">123</span>
                   </div>
                  <InputMask
                    mask="aaa-9*99"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#062D4E] transition-colors placeholder-gray-300 uppercase h-[52px]"
                    placeholder="AAA-0000"
                    maskChar={null} 
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Insira a placa no formato padrão Mercosul ou antigo.
                </p>
              </div>
            </div>
          </section>

          {/* Action Button */}
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className={`w-full bg-[#005694] hover:bg-[#004475] text-white font-bold h-14 rounded-lg flex items-center justify-center gap-3 transition-colors shadow-sm active:scale-[0.99] ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
               <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
               <Lock className="h-5 w-5" />
            )}
            {isLoading ? 'ENVIANDO...' : 'ABRIR CANCELA'}
          </button>
          
           <div className="w-full text-center">
             <p className="text-xs text-gray-400">
               Ação registrada automaticamente no log de sistema.
             </p>
           </div>

        </div>
        
        {/* Footer Version */}
        <div className="bg-gray-50 px-8 py-3 text-right flex-shrink-0">
             <span className="text-xs text-gray-400">v2.4.1</span>
        </div>

      </div>
    </div>
  );
};

export default CancelaManual;
